import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  getNewsletterCampaign,
  updateNewsletterCampaign,
  sendTestNewsletter,
  sendNewsletterCampaign,
} from "@/lib/api/newsletter.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { NewsletterAdminNav } from "@/components/admin/NewsletterAdminNav";
import { FormActions } from "@/components/FormActions";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { CraftEditor, type CraftEditorHandle } from "@/components/admin/CraftEditor";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { stringifyCraftState } from "@/lib/craft/serialize";
import { resolveCraftForSave } from "@/lib/craft/use-craft-save";
import { useAdminUser } from "@/lib/auth/useAdminUser";
import { canSendNewsletter } from "@/lib/auth/permissions";

export const Route = createFileRoute("/admin/newsletter/newsletters/$id")({
  loader: ({ params }) => getNewsletterCampaign({ data: { id: params.id } }),
  component: EditNewsletterPage,
});

function EditNewsletterPage() {
  const campaign = Route.useLoaderData();
  const router = useRouter();
  const adminUser = useAdminUser();
  const canSend = canSendNewsletter(adminUser.permissions);
  if (!campaign) throw notFound();

  const isDraft = campaign.status === "draft" || campaign.status === "failed";
  const [subject, setSubject] = useState(campaign.subject);
  const [previewText, setPreviewText] = useState(campaign.previewText ?? "");
  const [contentFormat, setContentFormat] = useState(campaign.contentFormat);
  const [html, setHtml] = useState(campaign.contentHtml ?? "");
  const [craftState, setCraftState] = useState(stringifyCraftState(campaign.craftState));
  const [craftCustomCss, setCraftCustomCss] = useState(campaign.craftCustomCss ?? "");
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const craftEditorRef = useRef<CraftEditorHandle>(null);

  async function handleSave() {
    const craft = resolveCraftForSave(craftEditorRef, { craftState, craftCustomCss });
    setSaving(true);
    try {
      await updateNewsletterCampaign({
        data: {
          id: campaign.id,
          subject,
          previewText,
          contentFormat,
          contentHtml: html,
          craftState: craft.serialized,
          craftCustomCss: craft.customCss,
        },
      });
      toast.success("Saved");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestSend() {
    if (!testEmail.trim()) {
      toast.error("Enter a test email address");
      return;
    }
    setSendingTest(true);
    try {
      if (isDraft) await handleSave();
      await sendTestNewsletter({ data: { id: campaign.id, email: testEmail.trim() } });
      toast.success("Test email sent");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Test send failed");
    } finally {
      setSendingTest(false);
    }
  }

  async function handleBlast() {
    setBlasting(true);
    try {
      if (isDraft) await handleSave();
      const result = await sendNewsletterCampaign({ data: { id: campaign.id } });
      toast.success(`Sent to ${result.sentCount} of ${result.totalRecipients} subscribers`);
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Send failed");
    } finally {
      setBlasting(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Edit newsletter" />
      <NewsletterAdminNav />

      {!isDraft ? (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Delivery stats</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-2xl font-bold">{campaign.sentCount.toLocaleString()}</p>
              <p className="text-muted-foreground">Successfully reached</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{campaign.failedCount.toLocaleString()}</p>
              <p className="text-muted-foreground">Failed</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{campaign.totalRecipients.toLocaleString()}</p>
              <p className="text-muted-foreground">Total recipients</p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <ContentAdminTabs
        settingsLabel="Email settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!isDraft} />
            </div>
            <div className="space-y-2">
              <Label>Preview text</Label>
              <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} disabled={!isDraft} />
            </div>
            <div className="space-y-2">
              <Label>Editor format</Label>
              <Select value={contentFormat} onValueChange={(v) => setContentFormat(v as "html" | "craft")} disabled={!isDraft}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="html">Rich text</SelectItem>
                  <SelectItem value="craft">Craft.js</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canSend ? (
              <>
                <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                  <Label>Send test email</Label>
                  <FormActions className="w-full">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full max-w-sm"
                    />
                    <Button type="button" variant="outline" onClick={() => void handleTestSend()} disabled={sendingTest}>
                      {sendingTest ? "Sending…" : "Send test"}
                    </Button>
                  </FormActions>
                  <p className="text-xs text-muted-foreground">Sends a preview with [TEST] prefix. Saves draft first if needed.</p>
                </div>
                {isDraft ? (
                  <FormActions>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" disabled={blasting}>Send to all subscribers</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Send newsletter blast?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will email all subscribed leads. You can test first using the field above.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => void handleBlast()}>
                            {blasting ? "Sending…" : "Send now"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </FormActions>
                ) : null}
              </>
            ) : null}
          </div>
        }
        content={
          isDraft ? (
            contentFormat === "html" ? (
              <RichTextEditor value={html} onChange={setHtml} />
            ) : (
              <CraftEditor
                ref={craftEditorRef}
                key={campaign.id}
                className="w-full"
                initialState={campaign.craftState ?? undefined}
                contentHtml={campaign.contentHtml}
                customCss={craftCustomCss}
                onChange={(serialized, css) => {
                  setCraftState(serialized);
                  setCraftCustomCss(css);
                }}
                onCustomCssChange={setCraftCustomCss}
              />
            )
          ) : (
            <div
              className="prose prose-sm max-w-none rounded-md border bg-muted/20 p-6"
              dangerouslySetInnerHTML={{ __html: campaign.contentHtml ?? "" }}
            />
          )
        }
      />
      {isDraft ? (
        <AdminFormFooter cancelTo="/admin/newsletter/newsletters" onSave={handleSave} saving={saving} />
      ) : null}
    </AdminPageShell>
  );
}
